// client/App.tsx - FIXED BOTTOM NAV FOR P2P

import React, { useState, useEffect } from "react";
import "./global.css";

// Polyfill minimal process for browser-only bundles (some crypto libs check process.env)
if (typeof globalThis.process === "undefined") {
  (globalThis as any).process = { env: {} };
}

import { useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Import providers
import { LoginModalContext } from "@/hooks/use-login-modal";
import { WalletModalContext } from "@/hooks/use-wallet-modal";
import { WalletProvider } from "@/contexts/WalletContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Import components
import { Header } from "@/components/layout";
import { MobileBottomNav } from "@/components/layout";
import { LoginModal } from "@/components/LoginModal";
import { ExternalWalletModal } from "@/components/ExternalWalletModal";
import { PlaceholderPage } from "@/components/PlaceholderPage";

// Import pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Assets from "./pages/Assets";
import Wallet from "./pages/Wallet";
import Deposit from "./pages/Deposit";
import Withdraw from "./pages/Withdraw";
import WithdrawSuccess from "./pages/WithdrawSuccess";
import LimitOrder from "./pages/Limit-order";
import Web3 from "./pages/Web3";
import Transfer from "./pages/Transfer";
import Convert from "./pages/Convert";
import BackupSeed from "./pages/BackupSeed";
import Exchange from "./pages/Exchange";
import History from "./pages/History";
import Dashboard from "./pages/Dashboard";
import Swap from "./pages/Swap";
import TokenDetail from "./pages/TokenDetail";
import Chart from "./pages/Chart";
import Kyc from "./pages/Kyc";
import Admin from "./pages/Admin";
import Reward from "./pages/Reward";

// P2P Pages
import P2P from "./pages/P2P";
import P2PRegister from "./pages/P2PRegister";
import P2PSuccess from "./pages/P2PSuccess";
import P2PCancel from "./pages/P2PCancel";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

function AppContent() {
  const location = useLocation();
  
  const hideHeaderAndNav = location.pathname === "/" || 
                           location.pathname === "/login" || 
                           location.pathname === "/signup" ||
                           location.pathname === "/auth";

  // ✅ REMOVED isP2PPage - no longer needed
  // const isP2PPage = location.pathname.startsWith("/p2p");

  return (
    <div className={`min-h-screen flex flex-col bg-[#1E2329] text-[#EAECEF] ${hideHeaderAndNav ? '' : 'pb-16 md:pb-0'}`}>
      {!hideHeaderAndNav && <Header />}
      
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth" element={<Auth />} />
          
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/assets" element={<Assets />} />
          <Route path="/token/:symbol" element={<TokenDetail />} />
          <Route path="/swap" element={<Swap />} />
          <Route path="/chart" element={<Chart />} />
          <Route path="/markets" element={<Swap />} />
          <Route path="/exchange" element={<Exchange />} />
          <Route path="/web3" element={<Web3 />} />
          <Route path="/reward" element={<Reward />} />
          
          {/* P2P Routes - Bottom Nav now shows */}
          <Route path="/p2p" element={<P2P />} />
          <Route path="/p2p/register" element={<P2PRegister />} />
          <Route path="/p2p/success" element={<P2PSuccess />} />
          <Route path="/p2p/cancel" element={<P2PCancel />} />
          
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/*" element={<NotFound />} />
          <Route path="/kyc" element={<Kyc />} />
          
          <Route
            path="/trade"
            element={
              <PlaceholderPage
                title="Trade"
                description="Advanced trading interface coming soon."
              />
            }
          />
          <Route path="/limit-order" element={<LimitOrder />} />
          
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/deposit" element={<Deposit />} />
          <Route path="/withdraw" element={<Withdraw />} />
          <Route path="/withdraw-success" element={<WithdrawSuccess />} />
          <Route path="/transfer" element={<Transfer />} />
          <Route path="/convert" element={<Convert />} />
          
          <Route path="/backup-seed" element={<BackupSeed />} />
          <Route path="/history" element={<History />} />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      
      {/* ✅ FIXED: Bottom Nav shows on ALL pages including P2P */}
      {!hideHeaderAndNav && <MobileBottomNav />}
      
      <LoginModal />
      <ExternalWalletModal />
    </div>
  );
}

function App() {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <LoginModalContext.Provider
          value={{
            isOpen: loginModalOpen,
            openLoginModal: () => setLoginModalOpen(true),
            closeLoginModal: () => setLoginModalOpen(false),
          }}
        >
          <WalletModalContext.Provider
            value={{
              isOpen: walletModalOpen,
              openWalletModal: () => setWalletModalOpen(true),
              closeWalletModal: () => setWalletModalOpen(false),
            }}
          >
            <WalletProvider>
              <QueryClientProvider client={queryClient}>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <AppContent />
                </TooltipProvider>
              </QueryClientProvider>
            </WalletProvider>
          </WalletModalContext.Provider>
        </LoginModalContext.Provider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
